import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { ApiError } from "@wilco/api";
import { Button, Notice, Plate, SelectField, TabPanel, Tabs, TextField } from "@wilco/ui";
import { useEffect, useRef, useState, type ReactNode, type SubmitEvent } from "react";
import { formatDate, formatNumber, serverErrorText, t } from "../i18n.ts";
import { ConfirmDialog, type Confirm } from "../ConfirmDialog.tsx";
import { ScanBar } from "../scan/ScanBar.tsx";
import { problemCode, stockApi } from "./api.ts";
import { parseQty } from "./qty.ts";
import { countQuery, shelfQuery, STOCK } from "./queries.ts";
import type { CountOutcome, ShelfView, WarehouseCode } from "./types.ts";
import { ACTIVITY_WAREHOUSES } from "./warehouses.ts";

const route = getRouteApi("/app/stock/shelf");

export type ShelfMode = "receive" | "issue" | "count";

export interface NoticeState {
  tone: "info" | "success" | "warning" | "danger";
  title: string;
  body?: ReactNode;
  /** Удачный подсчёт: галочка с перелётом (СК-25). */
  pop?: boolean;
}


/** Текст ошибки: detail сервера на языке пользователя, иначе каталог по коду. */
export function errorNotice(e: unknown): NoticeState {
  if (e instanceof ApiError) {
    const code = e.problem?.code;
    // Текст по коду из того же каталога, что у сервера, с аргументами
    // ответа; detail сервера, если каталог кода не знает.
    const args = e.problem?.args as Record<string, string | number> | undefined;
    const text = (code ? serverErrorText(code, args) : undefined) ?? e.problem?.detail ?? t("error.server.lead");
    return { tone: "danger", title: text };
  }
  return { tone: "danger", title: t("error.server.title"), body: t("error.server.lead") };
}

/**
 * «Полиця» (chmk_base, shelf): скан полки, приёмка на неё, «Видати і
 * списати» и подсчёт вслепую. Итог под полем, а не всплывашкой. После
 * действия фокус снова в поле скана: следующий код со сканера идёт туда.
 */
export function ShelfPage() {
  const { code, mode = "receive" } = route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scanRef = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [log, setLog] = useState<string[]>([]);

  const shelf = useQuery({ ...shelfQuery(code ?? ""), enabled: Boolean(code) && mode !== "count" });

  const scan = async (raw: string) => {
    setNotice(null);
    try {
      const r = await stockApi.scan(raw);
      if (r.kind === "product") {
        await navigate({ to: "/stock/product/$sku", params: { sku: r.sku } });
        return;
      }
      await navigate({ to: "/stock/shelf", search: { code: r.code, mode }, replace: true });
    } catch (e) {
      setNotice(errorNotice(e));
    }
  };

  const setMode = (m: ShelfMode) => {
    setNotice(null);
    void navigate({ to: "/stock/shelf", search: { code, mode: m }, replace: true });
  };

  const done = (n: NoticeState, line: string) => {
    setNotice(n);
    setLog((prev) => [line, ...prev].slice(0, 8));
    void queryClient.invalidateQueries({ queryKey: STOCK });
    scanRef.current?.focus();
  };

  const loadError = shelf.error ? errorNotice(shelf.error) : null;

  return (
    <div className="page stock-narrow">
      <h1 className="page-title">{t("shelf.title")}</h1>
      <ScanBar onScan={(c) => void scan(c)} placeholder={t("shelf.scan")} autoFocus={!code} inputRef={scanRef} />

      {notice ? (
        <Notice tone={notice.tone} title={notice.title} className={notice.pop ? "stock-notice stock-pop" : "stock-notice"}>
          {notice.body}
        </Notice>
      ) : loadError ? (
        <Notice tone="danger" title={loadError.title} className="stock-notice" />
      ) : null}

      {!code ? <p className="w-muted stock-hint">{t("shelf.empty")}</p> : null}

      {code ? (
        <Tabs<ShelfMode>
          value={mode}
          onValueChange={setMode}
          label={t("shelf.modes")}
          items={[
            { value: "receive", label: t("shelf.mode.receive") },
            { value: "issue", label: t("shelf.mode.issue") },
            { value: "count", label: t("shelf.mode.count") },
          ]}
        >
          {mode !== "count" && shelf.data ? <ShelfCard shelf={shelf.data} /> : null}
          <TabPanel value="receive">{shelf.data ? <ReceiveForm shelf={shelf.data} onDone={done} onScan={scan} /> : null}</TabPanel>
          <TabPanel value="issue">{shelf.data ? <IssueWriteOffForm shelf={shelf.data} onDone={done} onScan={scan} /> : null}</TabPanel>
          <TabPanel value="count">
            <CountForm code={code} onDone={done} onScan={scan} />
          </TabPanel>
        </Tabs>
      ) : null}

      {log.length > 0 ? (
        <section className="stock-log" aria-labelledby="shelf-log">
          <h2 id="shelf-log" className="desk-group-title">
            {t("shelf.log")}
          </h2>
          <ul>
            {log.map((line, i) => (
              <li key={`${String(i)}-${line}`}>{line}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/**
 * Карточка полки: код крупно, как на этикетке, которую с ним сверяют.
 * Новый код это новый узел, и карточка собирается от среза заново.
 */
function ShelfCard({ shelf }: { shelf: ShelfView }) {
  const free = shelf.onShelf - shelf.reserved;
  return (
    <Plate key={shelf.code} cut="md" bordered className="shelf-card w-build-in">
      <div className="shelf-card-head">
        <span className="shelf-code w-num">{shelf.code}</span>
        <span className="w-muted">{shelf.warehouse.name}</span>
      </div>
      <div className="shelf-product">
        <span className="w-num shelf-sku">{shelf.product.sku}</span> {shelf.product.name}
      </div>
      <dl className="shelf-stats">
        <Stat label={t("shelf.onShelf")} value={shelf.onShelf} uom={shelf.product.uom} />
        <Stat label={t("shelf.expected")} value={shelf.expectedTotal} uom={shelf.product.uom} />
        {shelf.onLine !== null ? <Stat label={t("shelf.onLine")} value={shelf.onLine} uom={shelf.product.uom} /> : null}
      </dl>
      {shelf.reserved > 0 ? (
        <p className="shelf-reserved">
          {t("shelf.reserved", { free: formatNumber(free), holders: shelf.reservedBy.join(", ") })}
        </p>
      ) : null}
    </Plate>
  );
}

function Stat({ label, value, uom }: { label: string; value: number; uom: string }) {
  return (
    <div className="shelf-stat">
      <dt>{label}</dt>
      <dd className="w-num">
        {formatNumber(value)}
        <small>{`\u00a0${uom}`}</small>
      </dd>
    </div>
  );
}

interface FormProps {
  onDone: (n: NoticeState, line: string) => void;
  onScan: (code: string) => Promise<void>;
}

/**
 * Поле количества с кнопкой действия. Нечисловое значение по Enter это скан
 * полки, попавший в поле (chmk_base, shelf.js): экран открывает ту полку.
 * Значение выделяется при фокусе: подставлено «5», набирают «50» и без
 * выделения получили бы «550».
 */
function QtyAction({
  label,
  uom,
  action,
  busy,
  onSubmit,
  onScan,
  children,
}: {
  label: string;
  uom: string;
  action: string;
  busy: boolean;
  onSubmit: (qty: number) => void;
  onScan: (code: string) => Promise<void>;
  children?: ReactNode;
}) {
  const [raw, setRaw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    const value = raw.trim();
    const qty = parseQty(value);
    if (qty === null) {
      if (value) {
        setRaw("");
        void onScan(value);
      } else {
        setError(t("shelf.qtyRequired"));
      }
      return;
    }
    setError(null);
    setRaw("");
    onSubmit(qty);
  };

  return (
    <form className="qty-form" onSubmit={submit}>
      {children}
      <div className="qty-row">
        <TextField
          ref={ref}
          label={label}
          inputMode="decimal"
          autoComplete="off"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onFocus={(e) => e.target.select()}
          error={error ?? undefined}
          className="qty-field"
          enterKeyHint="done"
        />
        <span className="qty-uom">{uom}</span>
        <Button type="submit" variant="primary" disabled={busy} className="qty-submit">
          {action}
        </Button>
      </div>
    </form>
  );
}

function ReceiveForm({ shelf, onDone, onScan }: FormProps & { shelf: ShelfView }) {
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const uom = shelf.product.uom;
  const receive = useMutation({ mutationFn: (v: { qty: number; confirmed: boolean; withoutOrder: boolean }) => stockApi.receive(shelf.id, v) });

  // Сервер спрашивает подтверждение кодом правила; экран переспрашивает
  // человека и шлёт то же количество с подтверждением (INV-10, INV-11).
  const run = (qty: number, confirmed = false, withoutOrder = false) => {
    receive.mutate(
      { qty, confirmed, withoutOrder },
      {
        onSuccess: (r) => {
          // Сервер говорит, сколько зарезервировано за заявками, но не за какими.
          const parts = r.reservedFor.map((x) =>
            x.request ? t("receive.reservedFor", { qty: formatNumber(x.qty), request: x.request, warehouse: x.warehouse }) : t("receive.reservedTotal", { qty: formatNumber(x.qty) }),
          );
          if (r.free > 0 && r.reservedFor.length > 0) parts.push(t("receive.free", { qty: formatNumber(r.free) }));
          if (r.expectedLeft > 0) parts.push(t("receive.expectedLeft", { qty: formatNumber(r.expectedLeft), uom }));
          if (r.priceMissing) parts.push(t("receive.priceMissing"));
          const title = r.withoutOrder
            ? t("receive.doneWithoutOrder", { qty: formatNumber(r.received), uom, shelf: shelf.code })
            : t("receive.done", { qty: formatNumber(r.received), uom, shelf: shelf.code });
          onDone({ tone: r.priceMissing ? "warning" : "success", title, body: parts.length ? parts.join(" ") : undefined }, title);
        },
        onError: (e) => {
          const code = problemCode(e);
          const detail = e instanceof ApiError ? (e.problem?.detail ?? "") : "";
          if (code === "receipt.no_expected") {
            setConfirm({ title: t("receive.withoutOrder.title"), body: t("receive.withoutOrder.body"), action: t("receive.withoutOrder.action"), run: () => run(qty, confirmed, true) });
          } else if (code === "receipt.over_expected") {
            setConfirm({ title: t("receive.over.title"), body: detail, action: t("receive.over.action"), run: () => run(qty, true, withoutOrder) });
          } else if (code === "receipt.looks_like_barcode") {
            setConfirm({ title: t("qty.barcode.title"), body: detail, action: t("qty.barcode.action"), run: () => run(qty, true, withoutOrder) });
          } else {
            onDone(errorNotice(e), errorNotice(e).title);
          }
        },
      },
    );
  };

  return (
    <div className="stock-form">
      {shelf.expected.length > 0 ? (
        <ul className="expected-list" aria-label={t("receive.expectedBy")}>
          {shelf.expected.map((e) => (
            <li key={e.order}>
              <span className="expected-doc">
                <span className="w-num">{e.order}</span>
                {` · ${e.supplier} · ${formatDate(e.due)}`}
              </span>
              <span className="expected-qty w-num">{`${formatNumber(e.qty)}\u00a0${uom}`}</span>
            </li>
          ))}
        </ul>
      ) : (
        <Notice tone="warning" title={t("receive.noExpected")} />
      )}
      <QtyAction label={t("receive.qty")} uom={uom} action={t("shelf.mode.receive")} busy={receive.isPending} onSubmit={(q) => run(q)} onScan={onScan} />
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

/** «Видати і списати» мелочь с «Основного» на склад деятельности одним действием (H2). */
function IssueWriteOffForm({ shelf, onDone, onScan }: FormProps & { shelf: ShelfView }) {
  const [warehouse, setWarehouse] = useState<WarehouseCode>("SER");
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const mutation = useMutation({ mutationFn: (qty: number) => stockApi.issueWriteOff(shelf, { warehouse, qty, reason }) });
  const uom = shelf.product.uom;

  const run = (qty: number) => {
    if (!reason.trim()) {
      setReasonError(t("issueWriteOff.reasonRequired"));
      return;
    }
    setReasonError(null);
    mutation.mutate(qty, {
      onSuccess: (r) => {
        const title = t("issueWriteOff.done", { qty: formatNumber(r.qty), uom, warehouse: r.warehouse });
        setReason("");
        onDone({ tone: "success", title, body: reason }, title);
      },
      onError: (e) => onDone(errorNotice(e), errorNotice(e).title),
    });
  };

  return (
    <div className="stock-form">
      <p className="w-muted">{t("issueWriteOff.lead")}</p>
      <QtyAction label={t("issueWriteOff.qty")} uom={uom} action={t("issueWriteOff.action")} busy={mutation.isPending} onSubmit={run} onScan={onScan}>
        <SelectField
          label={t("issueWriteOff.warehouse")}
          value={warehouse}
          onChange={(e) => setWarehouse(e.target.value as WarehouseCode)}
          options={ACTIVITY_WAREHOUSES.map((w) => ({ value: w.code, label: w.name }))}
        />
        <TextField
          label={t("issueWriteOff.reason")}
          placeholder={t("issueWriteOff.reasonHint")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={reasonError ?? undefined}
        />
      </QtyAction>
    </div>
  );
}

const OUTCOME: Record<CountOutcome, { tone: NoticeState["tone"]; key: "count.matched" | "count.notMatching" | "count.pending" | "count.applied" }> = {
  MATCHED: { tone: "success", key: "count.matched" },
  APPLIED: { tone: "success", key: "count.applied" },
  NOT_MATCHING: { tone: "warning", key: "count.notMatching" },
  PENDING_REVIEW: { tone: "info", key: "count.pending" },
};

/**
 * Подсчёт вслепую: остатка нет ни на экране, ни в ответе (INV-30). «Не
 * сходиться» без величины и знака (INV-31), повтор поверх ожидающего не
 * говорит, совпал ли он (INV-32).
 */
function CountForm({ code, onDone, onScan }: FormProps & { code: string }) {
  const view = useQuery(countQuery(code));
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const mutation = useMutation({ mutationFn: (v: { qty: number; confirmed: boolean }) => stockApi.count(view.data?.id ?? code, v) });

  const run = (qty: number, confirmed = false) => {
    mutation.mutate(
      { qty, confirmed },
      {
        onSuccess: ({ outcome }) => {
          const o = OUTCOME[outcome];
          const title = t(o.key, { shelf: code });
          onDone({ tone: o.tone, title, pop: outcome === "MATCHED" || outcome === "APPLIED" }, title);
        },
        onError: (e) => {
          if (problemCode(e) === "count.looks_like_barcode") {
            const detail = e instanceof ApiError ? (e.problem?.detail ?? "") : "";
            setConfirm({ title: t("qty.barcode.title"), body: detail, action: t("qty.barcode.action"), run: () => run(qty, true) });
          } else {
            onDone(errorNotice(e), errorNotice(e).title);
          }
        },
      },
    );
  };

  const v = view.data;
  if (!v) return null;
  return (
    <div className="stock-form">
      <Plate key={v.code} cut="md" bordered className="shelf-card w-build-in">
        <div className="shelf-card-head">
          <span className="shelf-code w-num">{v.code}</span>
        </div>
        <div className="shelf-product">
          <span className="w-num shelf-sku">{v.product.sku}</span> {v.product.name}
        </div>
        <p className="w-muted">
          {v.lastCount ? t("count.last", { date: formatDate(v.lastCount) }) : t("count.never")}
          {v.pending ? ` ${t("count.pendingNote")}` : ""}
        </p>
      </Plate>
      <QtyAction label={t("count.qty")} uom={v.product.uom} action={t("count.action")} busy={mutation.isPending} onSubmit={(q) => run(q)} onScan={onScan} />
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
